import { type ReactElement, ReactNode } from "react";
import { useWindowWidth } from "@react-hook/window-size";
import UserRole from "@olivahealth/business/src/Core/domain/value-objects/UserRole";
import { MOBILE_BREAKPOINT } from "../../../utils/breakpoints";
import { TextColor } from "../../atoms/Text";
import NavbarMobile from "./NavbarMobile";
import NavbarDesktop from "./NavbarDesktop";

export enum NavbarLinkType {
  PRIMARY = "primary",
  SECONDARY = "secondary",
  ORGANISATION_ADMIN = "organisation_admin",
  ADMIN = "admin",
}

export interface NavbarLink {
  activeRegex?: string;
  href?: string;
  icon?: ReactNode;
  isExternal?: boolean;
  label: string;
  role: UserRole[];
  type: NavbarLinkType;
  id?: string;
  rightSideIcon?: ReactNode;
  color?: TextColor;
  variant?: "light" | "dark";
}

export interface NavbarProps {
  isOrganisationAdmin?: boolean;
  hasTeamSessionsOnly?: boolean;
  labels: {
    admin: string;
    company: string;
  };
  links: NavbarLink[];
  variant: "light" | "dark";
}

/**
 * On mobile the Navbar is a dropdown menu, on desktop it's a vertical list.
 * Administration section  is rendered only on desktop.
 */
export default function Navbar({
  isOrganisationAdmin,
  hasTeamSessionsOnly,
  labels,
  links,
  variant,
}: NavbarProps): ReactElement {
  const isMobile = useWindowWidth() < MOBILE_BREAKPOINT;
  const {
    adminLinks,
    mobileLinks,
    organisationAdminLinks,
    primaryLinks,
    secondaryLinks,
  } = getLinks(links);

  if (isMobile && mobileLinks.length > 0) {
    return (
      <NavbarMobile
        mobileLinks={mobileLinks}
        organisationAdminLinks={organisationAdminLinks}
        isOrganisationAdmin={isOrganisationAdmin}
        labels={labels}
        variant={variant}
      />
    );
  }

  return (
    <NavbarDesktop
      adminLinks={adminLinks}
      organisationAdminLinks={organisationAdminLinks}
      primaryLinks={primaryLinks}
      secondaryLinks={secondaryLinks}
      isOrganisationAdmin={isOrganisationAdmin}
      hasTeamSessionsOnly={hasTeamSessionsOnly}
      labels={labels}
      variant={variant}
    />
  );
}

interface Links {
  adminLinks: NavbarLink[];
  mobileLinks: NavbarLink[];
  organisationAdminLinks: NavbarLink[];
  primaryLinks: NavbarLink[];
  secondaryLinks: NavbarLink[];
}

export function getLinks(links: NavbarLink[]): Links {
  const mobileLinks = links.filter(
    (link) =>
      link.type === NavbarLinkType.PRIMARY ||
      link.type === NavbarLinkType.SECONDARY,
  );
  const organisationAdminLinks = links.filter(
    (link) =>
      link.type === NavbarLinkType.ORGANISATION_ADMIN &&
      link.role.includes(UserRole.ORGANISATION_ADMIN),
  );
  const adminLinks = links.filter((link) => link.type === NavbarLinkType.ADMIN);
  const primaryLinks = links.filter(
    (link) => link.type === NavbarLinkType.PRIMARY,
  );
  const secondaryLinks = links.filter(
    (link) => link.type === NavbarLinkType.SECONDARY,
  );

  return {
    adminLinks,
    mobileLinks,
    organisationAdminLinks,
    primaryLinks,
    secondaryLinks,
  };
}
